/**
 * Fogatti Coupon
 *
 * 通用优惠券数据工具。
 * 负责读取桥接数据、解析 Variant / Product Coupon 优先级，
 * 并生成可通过 Variant ID 查询的最终 Coupon 映射。
 */
window.FogattiCoupon = (() => {
  /**
   * 标准无优惠券状态。
   */
  function createDisabledCoupon(source = 'none') {
    return {
      enabled: false,
      source,
      discount: null,
      discountCode: null,
      eventName: null,
      countdown: null
    };
  }


  /**
   * 解析 Liquid 输出的 Coupon JSON 节点。
   */
  function parseDataElement(element) {
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('[Fogatti Coupon] 优惠券数据解析失败。', error);
      return null;
    }
  }


  /**
   * 从页面读取 Coupon Bridge 数据。
   *
   * 可通过 productId、scopeId 或 root 缩小查询范围。
   */
  function loadData({
    productId = null,
    scopeId = null,
    root = document
  } = {}) {
    let selector = '[data-fg-coupon-data]';

    if (scopeId !== null && scopeId !== undefined) {
      selector += `[data-scope-id="${CSS.escape(String(scopeId))}"]`;
    }

    if (productId !== null && productId !== undefined) {
      selector += `[data-product-id="${CSS.escape(String(productId))}"]`;
    }

    const element = root.querySelector(selector);

    if (!element) {
      console.warn('[Fogatti Coupon] 未找到优惠券数据节点。', {
        productId,
        scopeId
      });

      return null;
    }

    return parseDataElement(element);
  }


  /**
   * 判断 Coupon 是否完整有效。
   */
  function isComplete(coupon) {
    if (!coupon) return false;

    return (
      Number(coupon.discount) > 0 &&
      Boolean(coupon.discountCode) &&
      Boolean(coupon.eventName) &&
      Boolean(coupon.countdown)
    );
  }


  /**
   * 解析单个 Variant 最终使用的 Coupon。
   *
   * 规则：
   * - Variant discount 未设置：允许 fallback Product
   * - Variant discount <= 0：明确关闭，不 fallback
   * - Variant discount > 0：Variant 接管，配置不完整时也不 fallback
   */
  function resolve(productCoupon, variantCoupon) {
    const rawVariantDiscount = variantCoupon?.discount;

    const hasVariantDiscount =
      rawVariantDiscount !== null &&
      rawVariantDiscount !== undefined &&
      rawVariantDiscount !== '';

    if (hasVariantDiscount) {
      const discount = Number(rawVariantDiscount);

      if (!Number.isFinite(discount) || discount <= 0) {
        return createDisabledCoupon('variant');
      }

      if (!isComplete(variantCoupon)) {
        return createDisabledCoupon('variant');
      }

      return {
        enabled: true,
        source: 'variant',
        discount,
        discountCode: variantCoupon.discountCode,
        eventName: variantCoupon.eventName,
        countdown: variantCoupon.countdown
      };
    }

    if (isComplete(productCoupon)) {
      return {
        enabled: true,
        source: 'product',
        discount: Number(productCoupon.discount),
        discountCode: productCoupon.discountCode,
        eventName: productCoupon.eventName,
        countdown: productCoupon.countdown
      };
    }

    return createDisabledCoupon('none');
  }


  /**
   * 生成 Variant ID → Resolved Coupon 映射。
   */
  function buildMap(couponData) {
    if (!couponData) return {};

    const productCoupon = couponData.productCoupon ?? null;
    const variants = couponData.variants ?? {};
    const map = {};

    for (const [variantId, variantData] of Object.entries(variants)) {
      map[variantId] = resolve(
        productCoupon,
        variantData.coupon ?? null
      );
    }

    return map;
  }


  /**
   * 按 Variant ID 获取最终 Coupon。
   */
  function getByVariantId(couponMap, variantId) {
    if (!variantId) {
      return createDisabledCoupon('none');
    }

    return (
      couponMap[String(variantId)] ??
      createDisabledCoupon('none')
    );
  }


  /**
   * 只暴露外部真正需要使用的方法。
   */
  return {
    loadData,
    resolve,
    buildMap,
    getByVariantId
  };
})();
